import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowDownToLine } from "lucide-react";
import { Link } from "react-router-dom";
import api, { apiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

export default function Withdraw() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [wstatus, setWstatus] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank");
  const [list, setList] = useState([]);

  const load = () => {
    api.get("/wallet/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/withdrawals").then((r) => setList(r.data)).catch(() => {});
    api.get("/withdrawals/status").then((r) => setWstatus(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const verified = user?.kyc_status === "verified";
  const blocked = wstatus && (!wstatus.enabled || !wstatus.allowed_today);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/withdrawals", { amount: Number(amount), method });
      toast.success("Withdrawal requested!");
      setAmount(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Withdraw Earnings</h1>

      {!verified && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3" data-testid="withdraw-kyc-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">KYC required</p>
            <p className="text-sm text-amber-700">Withdrawals are enabled only after your KYC is verified.</p>
            <Link to="/dashboard/kyc"><Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700 text-white">Complete KYC</Button></Link>
          </div>
        </div>
      )}

      {blocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3" data-testid="withdraw-blocked-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">{!wstatus.enabled ? "Withdrawals are temporarily disabled" : "Not available today"}</p>
            <p className="text-sm text-amber-700">{!wstatus.enabled ? "Please check back later." : `Withdrawals are allowed only on: ${wstatus.day_names.join(", ")}.`}</p>
          </div>
        </div>
      )}

      <div className="bg-brand-gradient text-white rounded-2xl p-6">
        <p className="text-sm text-white/70">Available balance</p>
        <p className="font-mono-num font-extrabold text-4xl mt-1" data-testid="withdraw-available">{inr(summary?.available)}</p>
      </div>

      {wstatus && wstatus.bonus > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5" data-testid="withdraw-bonus-info">
          <p className="font-semibold text-teal-800">Signup bonus points: {wstatus.bonus}</p>
          <p className="text-sm text-teal-700">Aapki withdrawal me bonus ka {wstatus.bonus_percent}% ({inr(wstatus.bonus_on_next)}) automatically add ho jayega.</p>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <div>
          <label className="text-sm font-semibold text-slate-600">Amount (₹)</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" className="mt-1.5 h-11 font-mono-num" placeholder={`Minimum ₹${wstatus?.min ?? 300}`} />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-600">Method</label>
          <div className="flex gap-2 mt-1.5">
            {["bank", "upi"].map((m) => (
              <button type="button" key={m} onClick={() => setMethod(m)} data-testid={`withdraw-method-${m}`}
                className={`flex-1 h-11 rounded-xl font-semibold capitalize ${method === m ? "bg-[#0B223D] text-white" : "bg-slate-100 text-slate-600"}`}>{m === "bank" ? "Bank Account" : "UPI"}</button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={!verified || blocked} data-testid="withdraw-submit-btn" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-12 rounded-xl">
          <ArrowDownToLine className="w-4 h-4 mr-1.5" /> Request withdrawal
        </Button>
      </form>

      <div>
        <h3 className="font-display font-bold text-lg text-[#0B223D] mb-3">Withdrawal history</h3>
        {list.length === 0 ? <p className="text-slate-400 text-sm">No withdrawals yet.</p> : (
          <div className="space-y-2">
            {list.map((w) => (
              <div key={w.id} className="bg-white border border-slate-200 rounded-xl p-4 flex justify-between items-center" data-testid={`withdrawal-${w.id}`}>
                <div><p className="font-mono-num font-bold text-[#0B223D]">{inr(w.amount)}</p><p className="text-xs text-slate-400 uppercase">{w.method} · {new Date(w.created_at).toLocaleDateString("en-IN")}</p></div>
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 capitalize">{w.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
